// src/pages/MatchResults.js
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getDocs, getDoc, doc, query, where } from "firebase/firestore";
import Panel from "../central/Panel";
import { col, db } from "../lib/fw";
import { paths } from "../lib/paths";
import { getLeagueConfig } from "../utils/leagueConfig";
import { getCurrentGW } from "../utils/gameweekHelpers";
import PageLoader from "../components/PageLoader";
import { useSeason } from "../context/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";
import { computeMatchResult, RESULT_COLORS } from "../components/matchInput/MatchScoreStep";

export default function MatchResults() {
  const { leagueID, gameweek } = useParams();
  const { selectedSeasonID } = useSeason();
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState({});
  const [leagueConfig, setLeagueConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvedGW, setResolvedGW] = useState(gameweek);
  const [gameweeks, setGameweeks] = useState([]);
  const [currentGW, setCurrentGW] = useState(null);
  // Tracks the most recently kicked-off loadMatches() call, so a slower,
  // stale request (e.g. from a season you've since toggled away from)
  // can't overwrite state after a newer one has already resolved.
  const requestIdRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const gw = await getCurrentGW(leagueID);
      if (isMounted) {
        setCurrentGW(gw);
        if (!gameweek) setResolvedGW(gw);
      }
    })();
    return () => { isMounted = false; };
  }, [leagueID, gameweek]);

  useEffect(() => {
    if (!resolvedGW) return;
    const requestId = ++requestIdRef.current;
    loadMatches(resolvedGW, requestId);
  }, [resolvedGW, selectedSeasonID]);

  async function loadMatches(gw, requestId) {
    setLoading(true);
    const isCurrent = () => requestIdRef.current === requestId;
    try {
      // league doc: leagues/{leagueID}
      const leagueDoc = await getDoc(doc(db, "leagues", leagueID));
      const league = leagueDoc.exists() ? leagueDoc.data() : null;
      const config = league ? getLeagueConfig(league.type) : null;

      // matches: leagues/{leagueID}/gameweeks/{gameweek}/matches
      const matchSnap = await getDocs(paths.gwMatches(leagueID, gw, selectedSeasonID));
      const matchData = matchSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // players: leagues/{leagueID}/players
      const playerSnap = await getDocs(paths.leaguePlayers(leagueID, selectedSeasonID));
      const playerMap = {};
      playerSnap.docs.forEach(d => {
        const p = d.data();
        playerMap[d.id] = { name: p.name || d.id, position: p.position || null };
      });

      // gameweeks: leagues/{leagueID}/gameweeks (exclude current/in-progress)
      const gwSnap = await getDocs(paths.leagueGameweeks(leagueID, selectedSeasonID));
      const currentGWID = await getCurrentGW(leagueID);
      const gwList = gwSnap.docs
        .map(d => d.id)
        .filter(id => id !== currentGWID)
        .sort();

      if (isCurrent()) {
        setLeagueConfig(config);
        setMatches(matchData);
        setPlayers(playerMap);
        setGameweeks(gwList);
      }

      // helpful console diagnostics
      console.log(
        "[MatchResults] path:",
        paths.gwMatches(leagueID, gw).join("/"),
        "matches:", matchData.length,
        "players:", playerSnap.size,
        "leagueConfigType:", config?.type
      );
    } catch (err) {
      console.error("Error fetching match results:", err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }

  // Defensive fetch players by IDs if players map is empty
  useEffect(() => {
    if (matches.length === 0) return;
    if (Object.keys(players).length > 0) return;

    let isMounted = true;

    (async () => {
      try {
        const playerIDs = new Set();

        matches.forEach(m => {
          // appearanceData keys
          if (m.appearanceData) {
            Object.keys(m.appearanceData).forEach(pid => playerIDs.add(pid));
          }
          // goals scorer and assist
          if (Array.isArray(m.goals)) {
            m.goals.forEach(g => {
              if (g.scorer && g.scorer !== "OWN_GOAL") playerIDs.add(g.scorer);
              if (g.assist) playerIDs.add(g.assist);
            });
          }
          // bestPlayers
          if (m.bestPlayers) {
            Object.values(m.bestPlayers).forEach(pid => {
              if (pid) playerIDs.add(pid);
            });
          }
          // infractionAssignments
          if (m.infractionAssignments) {
            Object.values(m.infractionAssignments).forEach(arr => {
              if (Array.isArray(arr)) {
                arr.forEach(pid => playerIDs.add(pid));
              } else if (arr) {
                playerIDs.add(arr);
              }
            });
          }
        });

        if (playerIDs.size === 0) return;

        // Query players by IDs
        const playersRef = paths.leaguePlayers(leagueID, selectedSeasonID);
        const chunkSize = 10; // Firestore 'in' queries limit to 10
        const playerMap = {};

        const idsArray = Array.from(playerIDs);
        for (let i = 0; i < idsArray.length; i += chunkSize) {
          const chunk = idsArray.slice(i, i + chunkSize);
          const q = query(playersRef, where("__name__", "in", chunk));
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            const p = d.data();
            playerMap[d.id] = { name: p.name || d.id, position: p.position || null };
          });
        }

        if (isMounted) {
          setPlayers(playerMap);
          console.log("[MatchResults] Defensive player fetch, loaded players:", Object.keys(playerMap).length);
        }
      } catch (err) {
        console.error("Error fetching players defensively:", err);
      }
    })();

    return () => { isMounted = false; };
  }, [matches, players, leagueID]);

  const nameOf = (id) => {
    if (!id) return "Unknown";
    const p = players[id];
    if (p && p.name) return p.name;
    console.log(`[MatchResults] nameOf: No name found for ID "${id}"`);
    return id; // fallback to id
  };

  const positionOf = (id) => {
    if (!id) return null;
    const p = players[id];
    if (p && p.position) return p.position;
    console.log(`[MatchResults] positionOf: No position found for ID "${id}"`);
    return null;
  };

  if (loading) {
    return (
      <>
        <PageLoader message="Loading match results…" />
      </>
    );
  }

  const gwIndex = gameweeks.indexOf(resolvedGW);
  const canPrevGW = gwIndex > 0;
  const canNextGW = gwIndex >= 0 && gwIndex < gameweeks.length - 1;
  const arrowBtnStyle = (enabled) => ({
    background: "none", border: "none", cursor: enabled ? "pointer" : "default",
    padding: "0.3rem 0.6rem", fontSize: "1rem",
    color: enabled ? "#50B4C8" : "#ccc", fontWeight: "700", lineHeight: 1,
  });
  const GWPill = () => (
    <div style={{ display: "inline-flex", alignItems: "center", background: "#f4f7f8", borderRadius: "999px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
      <button style={arrowBtnStyle(canPrevGW)} disabled={!canPrevGW}
        onClick={() => canPrevGW && setResolvedGW(gameweeks[gwIndex - 1])}
        aria-label="Previous gameweek">←</button>
      <span style={{ fontWeight: 600, fontSize: "0.85rem", minWidth: 44, textAlign: "center", color: "#222", padding: "0 0.1rem" }}>{resolvedGW || "—"}</span>
      <button style={arrowBtnStyle(canNextGW)} disabled={!canNextGW}
        onClick={() => canNextGW && setResolvedGW(gameweeks[gwIndex + 1])}
        aria-label="Next gameweek">→</button>
    </div>
  );

  if (matches.length === 0) {
    return (
      <Panel>
        <SeasonSelector />
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
          <GWPill />
        </div>
        <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⚽</div>
          <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
            No matches yet for {resolvedGW}
          </div>
        </div>
      </Panel>
    );
  }

  const sectionTitleStyle = { marginTop: "1.1rem", marginBottom: "0.35rem" };
  const listStyle = { listStyleType: "none", paddingLeft: 0, margin: 0, lineHeight: "1.35" };
  const itemStyle = { margin: "2px 0" };
  const dividerStyle = { border: "none", borderBottom: "1px solid #e5e7eb", margin: "0.75rem 0" };

  return (
    <>
      <Panel>
        <SeasonSelector />
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
          <GWPill />
        </div>

        {matches.map((m, idx) => (
          <div key={m.id} style={{ marginBottom: "2.5rem" }}>
            <h3 style={{ marginTop: "1.5rem" ,marginBottom: "0.5rem", textDecoration: "underline" }}>Match {idx + 1}</h3>

            {m?.yourScore !== undefined && m?.yourScore !== null && m?.theirScore !== undefined && m?.theirScore !== null && (
              <>
                <h3 style={sectionTitleStyle}>Result</h3>
                <div style={{ lineHeight: "1.35" }}>
                  {(() => {
                    const resultValue = m.result || computeMatchResult(m.yourScore, m.theirScore);
                    const opponentLabel = m.opponent && String(m.opponent).trim() !== "" ? m.opponent : "Opponent";
                    return (
                      <p style={{ margin: "2px 0" }}>
                        vs {opponentLabel}: {m.yourScore}–{m.theirScore}
                        {resultValue && (
                          <span
                            style={{
                              display: "inline-block",
                              marginLeft: 8,
                              padding: "1px 9px",
                              borderRadius: "999px",
                              background: `${RESULT_COLORS[resultValue]}1A`,
                              color: RESULT_COLORS[resultValue],
                              fontWeight: 700,
                              fontSize: "0.85em",
                            }}
                          >
                            {resultValue}
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>
                <hr style={dividerStyle} />
              </>
            )}

            <h3 style={sectionTitleStyle}>Appearances</h3>
            <ul style={listStyle}>
              {Object.entries(m.appearanceData || {}).map(([pid, data]) =>
                data?.appeared ? (
                  <li key={pid} style={itemStyle}>
                    {players[pid]?.name ? players[pid].name : pid} {data?.isGK ? "🧤" : ""}
                  </li>
                ) : null
              )}
            </ul>
            <hr style={dividerStyle} />

            {(m.starters && Object.keys(m.starters).some(pid => m.starters[pid])) && (
              <>
                <h3 style={sectionTitleStyle}>Starters</h3>
                <ul style={listStyle}>
                  {Object.keys(m.starters || {})
                    .filter((pid) => m.starters[pid])
                    .map((pid) => (
                      <li key={pid} style={itemStyle}>
                        {players[pid]?.name ? players[pid].name : pid} {m.appearanceData?.[pid]?.isGK ? "🧤" : ""}
                      </li>
                    ))}
                </ul>
                <hr style={dividerStyle} />
              </>
            )}

            <h3 style={sectionTitleStyle}>Goals</h3>
            <ul style={listStyle}>
              {(m.goals || []).map((g, i) => {
                const showGoal = leagueConfig?.rules?.goal !== false;
                const showAssist = leagueConfig?.rules?.assist !== false;
                if (!showGoal) return null;

                const isOG = g.scorer === "OWN_GOAL";
                const scorerName = isOG ? "Own Goal" : (players[g.scorer]?.name || g.scorer || "—");
                if (!isOG && !players[g.scorer]) {
                  console.log(`[MatchResults] Goals: No player found for scorer ID "${g.scorer}"`);
                }
                const assistName = g.assist ? (players[g.assist]?.name || g.assist) : "None";
                if (g.assist && !players[g.assist]) {
                  console.log(`[MatchResults] Goals: No player found for assist ID "${g.assist}"`);
                }

                return (
                  <li key={i} style={itemStyle}>
                    {scorerName} {showAssist ? `(Assist: ${assistName})` : null}
                  </li>
                );
              })}
            </ul>
            <hr style={dividerStyle} />

            {m.cleanSheet !== undefined && (
              <>
                <h3 style={sectionTitleStyle}>Clean Sheet</h3>
                <div style={{ lineHeight: "1.35" }}>
                  <p style={{ margin: "2px 0" }}>{m.cleanSheet ? "✅ Yes" : "❌ No"}</p>
                </div>
                <hr style={dividerStyle} />
              </>
            )}

            {(m.infractionAssignments && Object.keys(m.infractionAssignments).length > 0) && (
              <>
                <h3 style={sectionTitleStyle}>Infractions</h3>
                <ul style={listStyle}>
                  {Object.entries(m.infractionAssignments || {}).flatMap(([type, arr]) =>
                    (Array.isArray(arr) ? arr : [arr]).map((pid) => {
                      if (!players[pid]) {
                        console.log(`[MatchResults] Infractions: No player found for ID "${pid}"`);
                      }
                      return (
                        <li key={`${type}-${pid}`} style={itemStyle}>{type}: {players[pid]?.name || pid}</li>
                      );
                    })
                  )}
                </ul>
                <hr style={dividerStyle} />
              </>
            )}

            <h3 style={sectionTitleStyle}>Bonus Players</h3>
            <ul style={listStyle}>
              <li style={itemStyle}>
                1st Place: {m.bestPlayers && m.bestPlayers["1"] ? (players[m.bestPlayers["1"]]?.name || m.bestPlayers["1"]) : m.bestPlayers && m.bestPlayers["1"] ? m.bestPlayers["1"] : "—"}{m.bestPlayers && m.bestPlayers["1"] && players[m.bestPlayers["1"]] && players[m.bestPlayers["1"]].position ? ` (${players[m.bestPlayers["1"]].position})` : ""}
                {!players[m.bestPlayers?.["1"]] && m.bestPlayers?.["1"] ? console.log(`[MatchResults] Bonus Players: No player found for 1st place ID "${m.bestPlayers["1"]}"`) : null}
              </li>
              <li style={itemStyle}>
                2nd Place: {m.bestPlayers && m.bestPlayers["2"] ? (players[m.bestPlayers["2"]]?.name || m.bestPlayers["2"]) : m.bestPlayers && m.bestPlayers["2"] ? m.bestPlayers["2"] : "—"}{m.bestPlayers && m.bestPlayers["2"] && players[m.bestPlayers["2"]] && players[m.bestPlayers["2"]].position ? ` (${players[m.bestPlayers["2"]].position})` : ""}
                {!players[m.bestPlayers?.["2"]] && m.bestPlayers?.["2"] ? console.log(`[MatchResults] Bonus Players: No player found for 2nd place ID "${m.bestPlayers["2"]}"`) : null}
              </li>
              <li style={itemStyle}>
                3rd Place: {m.bestPlayers && m.bestPlayers["3"] ? (players[m.bestPlayers["3"]]?.name || m.bestPlayers["3"]) : m.bestPlayers && m.bestPlayers["3"] ? m.bestPlayers["3"] : "—"}{m.bestPlayers && m.bestPlayers["3"] && players[m.bestPlayers["3"]] && players[m.bestPlayers["3"]].position ? ` (${players[m.bestPlayers["3"]].position})` : ""}
                {!players[m.bestPlayers?.["3"]] && m.bestPlayers?.["3"] ? console.log(`[MatchResults] Bonus Players: No player found for 3rd place ID "${m.bestPlayers["3"]}"`) : null}
              </li>
            </ul>
          </div>
        ))}
      </Panel>
    </>
  );
}